import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const agents = [
  {
    role: '内容理解智能体',
    desc: '提取核心概念、问题链、案例链和价值表达，判断概念关系是否讲透',
    icon: 'ri-book-read-line',
    tier: 'input' as const,
  },
  {
    role: '课堂结构智能体',
    desc: '识别导入、讲授、案例、互动和总结的时间序列，还原课堂结构骨架',
    icon: 'ri-timeline-view',
    tier: 'input' as const,
  },
  {
    role: '互动识别智能体',
    desc: '处理提问、等待、回应和追问，识别互动链条的完整度',
    icon: 'ri-chat-smile-2-line',
    tier: 'input' as const,
  },
  {
    role: '资料关联智能体',
    desc: '连接政策、重大工程、历史事件和数据材料到权威来源与出处',
    icon: 'ri-folder-open-line',
    tier: 'process' as const,
  },
  {
    role: '评价智能体',
    desc: '将证据映射到观测点和维度，结合专家样本形成专业判断',
    icon: 'ri-scales-3-line',
    tier: 'process' as const,
  },
  {
    role: '报告与校验智能体',
    desc: '检查证据完整性，输出可追溯的评价报告与改进建议',
    icon: 'ri-shield-check-line',
    tier: 'output' as const,
  },
];

const tierMeta = {
  input: {
    label: '输入层',
    color: 'primary',
    iconBg: 'bg-primary-50 border-primary-200/50',
    iconText: 'text-primary-600',
    tagBg: 'bg-primary-100/60 border-primary-200/30',
    tagText: 'text-primary-700',
    badgeBg: 'bg-primary-50 border-primary-200/30',
    badgeText: 'text-primary-700',
    activeBg: 'bg-primary-50 border-primary-400',
    activeRing: 'ring-primary-300/50',
    bar: 'bg-primary-500',
  },
  process: {
    label: '处理层',
    color: 'accent',
    iconBg: 'bg-accent-50 border-accent-200/50',
    iconText: 'text-accent-600',
    tagBg: 'bg-accent-100/60 border-accent-200/30',
    tagText: 'text-accent-700',
    badgeBg: 'bg-accent-50 border-accent-200/30',
    badgeText: 'text-accent-700',
    activeBg: 'bg-accent-50 border-accent-400',
    activeRing: 'ring-accent-300/50',
    bar: 'bg-accent-500',
  },
  output: {
    label: '输出层',
    color: 'secondary',
    iconBg: 'bg-secondary-50 border-secondary-200/50',
    iconText: 'text-secondary-600',
    tagBg: 'bg-secondary-100/60 border-secondary-200/30',
    tagText: 'text-secondary-700',
    badgeBg: 'bg-secondary-50 border-secondary-200/30',
    badgeText: 'text-secondary-700',
    activeBg: 'bg-secondary-50 border-secondary-400',
    activeRing: 'ring-secondary-300/50',
    bar: 'bg-secondary-500',
  },
};

export default function P25AgentRoundtableNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden overflow-y-auto">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-20 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-16 left-10 w-[350px] h-[350px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-10 lg:px-16 flex flex-col py-6 md:py-8 lg:py-10 gap-4 md:gap-6">
        {/* 标题 */}
        <div ref={titleRef} className="text-center">
          <span className={`inline-flex items-center gap-2 text-lg font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />
            多智能体系统
          </span>
          <h2 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 mb-2 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            数字教研组：多智能体如何<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">协同评课</span>
          </h2>
          <p className={`text-lg md:text-xl text-foreground-950 font-body max-w-2xl mx-auto transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            六个智能体分工明确、相互校验
          </p>
        </div>

        {/* 六张智能体卡片 */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {agents.map((agent, i) => {
            const c = tierMeta[agent.tier];
            const isActive = activeIndex === i;
            return (
              <button
                key={agent.role}
                onClick={() => setActiveIndex(i)}
                className={`relative text-left rounded-xl p-3 md:p-4 border transition-all duration-300 cursor-pointer hover:-translate-y-0.5 ${
                  isActive
                    ? `${c.activeBg} ${c.activeRing} ring-2 shadow-sm`
                    : 'bg-background-50/40 backdrop-blur-md border-background-200/50 hover:border-background-300 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)]'
                }`}
                style={{ transitionDelay: `${350 + i * 80}ms` }}
              >
                {/* 顶部色条 */}
                <div className={`absolute top-0 left-4 right-4 h-0.5 rounded-full ${c.bar} ${isActive ? 'opacity-100' : 'opacity-40'}`} />

                <div className="flex items-start gap-3 mb-2">
                  <div className={`w-14 h-14 rounded-lg ${c.iconBg} flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`}>
                    <i className={`${agent.icon} text-3xl ${c.iconText}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={`font-heading text-3xl font-bold ${isActive ? 'text-foreground-900' : 'text-foreground-950'}`}>{agent.role}</h4>
                      <span className={`px-2 py-0.5 rounded text-sm font-label font-medium ${c.tagBg} ${c.tagText} border whitespace-nowrap`}>
                        {c.label}
                      </span>
                    </div>
                    <p className="text-sm text-foreground-950 font-body leading-snug">{agent.desc}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* 协同机制 */}
        <div className={`transition-all duration-700 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="rounded-2xl bg-background-100 border border-background-200/50 p-3 md:p-4">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-md bg-accent-50 border border-accent-200/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-links-line text-base text-accent-500" />
              </div>
              <p className="text-base md:text-lg text-foreground-950 font-body leading-relaxed">
                <span className="font-semibold text-foreground-950">相互约束：</span>
                视觉智能体看到学生围坐不能直接得出"互动充分"，还需声音智能体确认回应、文本智能体确认回应进入后续教学。
                多条证据指向一致时，系统才形成较强判断。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}